/**
 * Entorno de tenant test en el browser (genérico: duntower-test, nibiru-test, …).
 * No depende de VUE_APP: el mismo bundle puede servir prod (S3) y test (/var/www).
 *
 * Hosts `*-test.delegat.es` deben hablar con la API de test (rama `test`),
 * nunca con api2.delegat.es (prod).
 */

const DEFAULT_PROD_API = "https://api2.delegat.es";
const DEFAULT_TEST_API = "https://api2-test.delegat.es";

function stripTrailingSlash(url) {
  return String(url || "").replace(/\/$/, "");
}

function isLocalHostname(hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

export function getTenantSubdomainFromHostname(hostname = window.location.hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  if (!host) return "";
  if (
    host.endsWith(".delegat.es") ||
    host.endsWith(".delegat.test") ||
    host.endsWith(".iajur.com")
  ) {
    return host.split(".")[0] || "";
  }
  return "";
}

export function isTenantTestEnvironment(hostname = window.location.hostname) {
  const sub = getTenantSubdomainFromHostname(hostname);
  if (/-test$/i.test(sub)) return true;
  // Fallback local: subdomain en localStorage (npm run serve)
  if (isLocalHostname(hostname)) {
    try {
      const stored = String(localStorage.getItem("subdomain") || "").trim();
      return /-test$/i.test(stored);
    } catch (e) {
      return false;
    }
  }
  return false;
}

/**
 * Base HTTP(S) del API según el host del front (sin barra final).
 * - `*-test.delegat.es` → api2-test (override: VUE_APP_WENCES_API_URL_TEST)
 * - localhost → VUE_APP_WENCES_API_URL (npm run serve)
 * - resto → VUE_APP_WENCES_API_URL / api2 prod
 */
export function resolveWencesApiBaseUrl(hostname = typeof window !== "undefined" ? window.location.hostname : "") {
  const baked =
    stripTrailingSlash(process.env.VUE_APP_WENCES_API_URL) ||
    (typeof window === "undefined" ? "http://localhost:4000" : DEFAULT_PROD_API);

  if (isLocalHostname(hostname)) {
    return stripTrailingSlash(process.env.VUE_APP_WENCES_API_URL) || "http://localhost:4000";
  }

  if (isTenantTestEnvironment(hostname)) {
    return (
      stripTrailingSlash(process.env.VUE_APP_WENCES_API_URL_TEST) || DEFAULT_TEST_API
    );
  }

  return baked || DEFAULT_PROD_API;
}

/**
 * Base WS/WSS alineada con el API resuelto (sin path).
 * Override opcional: VUE_APP_WEBHOOK_API_URL_TEST / VUE_APP_WEBHOOK_API_URL.
 */
export function resolveWencesWebhookBaseUrl(
  hostname = typeof window !== "undefined" ? window.location.hostname : ""
) {
  if (isLocalHostname(hostname)) {
    return (
      stripTrailingSlash(process.env.VUE_APP_WEBHOOK_API_URL) ||
      stripTrailingSlash(process.env.VUE_APP_WENCES_API_URL) ||
      "ws://localhost:4000"
    );
  }

  if (isTenantTestEnvironment(hostname)) {
    const explicit = stripTrailingSlash(process.env.VUE_APP_WEBHOOK_API_URL_TEST);
    if (explicit) return explicit;
    const api = resolveWencesApiBaseUrl(hostname);
    return api.replace(/^http/i, "ws");
  }

  return (
    stripTrailingSlash(process.env.VUE_APP_WEBHOOK_API_URL) ||
    stripTrailingSlash(process.env.VUE_APP_WENCES_API_URL) ||
    DEFAULT_PROD_API.replace(/^http/i, "ws")
  );
}
